import { useState, useEffect, useCallback } from 'react';
import client from '../api/client.js';
import { useAuthStore } from '../store/auth.js';

export function useAuth() {
  const { user, token, setAuth, logout } = useAuthStore();
  // Always start in loading state — we must re-authenticate every launch
  // to handle account switching on shared devices (two Telegram accounts
  // share localStorage, so a stale token from account A must not be used
  // when account B opens the app).
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const authenticate = useCallback(async () => {
    setLoading(true);
    setError(null);

    try {
      const tg = window.Telegram?.WebApp;

      if (!tg) {
        // Development fallback: use a mock initData
        if (import.meta.env.DEV) {
          console.warn('Telegram WebApp not available – using mock auth');
          const response = await client.post('/auth/telegram', {
            initData: 'mock_init_data',
          });
          setAuth(response.data);
          return response.data;
        }
        throw new Error('Telegram WebApp is not available');
      }

      tg.ready();
      tg.expand();

      const initData = tg.initData;

      if (!initData) {
        throw new Error('No initData from Telegram');
      }

      const response = await client.post('/auth/telegram', { initData });
      setAuth(response.data);
      return response.data;
    } catch (err) {
      const message = err.response?.data?.error || err.message || 'Authentication failed';
      setError(message);
      throw err;
    } finally {
      setLoading(false);
    }
  }, [setAuth]);

  const signOut = useCallback(async () => {
    try {
      if (token) {
        await client.post('/auth/logout');
      }
    } catch {
      // Ignore errors on logout
    } finally {
      logout();
    }
  }, [token, logout]);

  // Always re-authenticate on every launch so that account switching on a
  // shared device works correctly (two Telegram accounts share localStorage).
  useEffect(() => {
    authenticate().catch(() => {});
  }, []);

  return {
    user,
    token,
    loading,
    error,
    isAuthenticated: !!token,
    authenticate,
    logout: signOut,
  };
}
